import { TEST_IDS } from '@va/constants';
import { useLocale } from '@va/localization';
import { addNumberSeparator } from '@va/util/helpers';
import { Chart, ChartConfiguration, ChartDataset } from 'chart.js';
import classNames from 'classnames';
import { assign } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import '../registerChartPlugins';

type PieChartProps = {
  id: string;
  className?: string;
  datasets: Array<ChartDataset<'pie'>>;
  labels: (string | string[])[];
  tooltipFn?: (ctx: any) => string;
  size?: number;
};

export const PieChart = ({ id, size, className, datasets, labels, tooltipFn }: PieChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart<'pie'>>(null);
  const { locale } = useLocale();

  const defaultTooltip = useCallback(
    (context: any) => {
      const label = labels[context.dataIndex];
      if (!label) return addNumberSeparator(context.raw, locale);
      return `${labels[context.dataIndex]}  ${addNumberSeparator(context.raw, locale)}`;
    },
    [labels, locale],
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvasContext = canvasRef.current.getContext('2d');

    if (!canvasContext) return;

    const config: ChartConfiguration<'pie'> = {
      type: 'pie',
      data: {
        labels: labels,
        datasets: datasets,
      },
      options: {
        plugins: {
          datalabels: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: tooltipFn || defaultTooltip,
            },
          },
        },
      },
    };

    if (!chartRef.current) {
      chartRef.current = new Chart<'pie'>(canvasContext, config);
      return;
    }

    updateChart(chartRef.current, config);
  }, [datasets, defaultTooltip, labels, tooltipFn]);

  return (
    <div className={classNames('', className)}>
      <canvas
        id={id}
        data-testid={TEST_IDS.helpers.createChartCanvasId(id)}
        ref={canvasRef}
        height={size}
        width={size}
      />
    </div>
  );
};

function updateChart(chart: Chart<'pie'>, config: ChartConfiguration<'pie'>) {
  // Update chart options
  if (config.options) {
    chart.options = config.options;
  }

  // Update chart datasets
  if (chart.data.datasets.length !== config.data.datasets.length) {
    // If the nr of datasets is different, reassign the object
    chart.data.datasets = config.data.datasets;
  } else {
    for (let i = 0; i < config.data.datasets.length; i++) {
      const dataset = config.data.datasets[i];
      assign(chart.data.datasets[i], dataset);
    }
  }

  // Update chart labels
  if (chart.data.labels) {
    chart.data.labels = config.data.labels;
  }

  // Re-draw chart
  chart.update();
}
